import { makeAutoObservable, runInAction } from 'mobx';
import {
  adminShiftsClose,
  adminShiftsGetOne,
  adminShiftsMemberActualTime,
  adminShiftsMemberFinish,
  adminShiftsMemberNoShow,
  adminShiftsMemberRemove,
  adminShiftsMemberReturn,
  adminShiftsMemberStart,
  adminShiftsOpen,
  adminShiftsUpdateResponsible,
  ShiftApiError,
} from '@/services/shiftsApi';
import { shiftsStore } from '@/stores/ShiftsStore';
import type { ShiftDetail } from '@/types/shift.types';
import {
  clockOnDateToIso,
  formatCloseViolationMessages,
  userFacingMutationError,
  validateActualClocks,
} from '@/pages/EmployeesPage/components/ShiftDay/shiftDayModel';
import { parseDateParam, weekBounds } from '@/pages/EmployeesPage/components/ShiftsWeek/shiftWeekModel';

const mutationMessage = (
  error: unknown,
  fallback: string,
): { message: string; details: string[]; violationDetails: ShiftApiError['violationDetails'] } => {
  if (error instanceof ShiftApiError) {
    return {
      message: error.message || fallback,
      details: error.details,
      violationDetails: error.violationDetails,
    };
  }
  return { message: fallback, details: [], violationDetails: [] };
};

/**
 * Экран дня существующей смены. Успех мутации заново читает GET.
 * 409 не записывается в detail: статус и состав остаются прежними.
 */
class ShiftViewStore {
  detail: ShiftDetail | null = null;
  isLoading = false;
  isRefreshing = false;
  loadError: string | null = null;
  actionError: string | null = null;
  closeViolations: string[] = [];
  isOpening = false;
  isClosing = false;
  responsibleSaving = false;
  responsiblePending: string | null | undefined = undefined;
  pendingMemberUuid: string | null = null;
  timeModalMemberUuid: string | null = null;
  timeStart = '';
  timeEnd = '';
  timeError: string | null = null;
  isSavingTime = false;

  private requestId = 0;
  private visibleUuid: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get isBusy(): boolean {
    return (
      this.responsibleSaving ||
      this.isOpening ||
      this.isClosing ||
      this.pendingMemberUuid !== null ||
      this.isSavingTime
    );
  }

  load = (uuid: string) => {
    this.visibleUuid = uuid;
    return this.fetch(uuid);
  };

  private fetch = async (uuid: string): Promise<boolean> => {
    if (!uuid) return false;
    const requestId = ++this.requestId;
    const sameShift = this.detail?.uuid === uuid;
    if (sameShift) {
      this.isRefreshing = true;
    } else {
      this.detail = null;
      this.isLoading = true;
      this.actionError = null;
      this.closeViolations = [];
      this.timeModalMemberUuid = null;
      this.responsiblePending = undefined;
    }
    this.loadError = null;

    try {
      const detail = await adminShiftsGetOne(uuid);
      if (requestId !== this.requestId || this.visibleUuid !== uuid) return false;
      runInAction(() => {
        this.detail = detail;
        this.isLoading = false;
        this.isRefreshing = false;
        this.loadError = null;
        this.actionError = null;
        this.closeViolations = [];
      });
      return true;
    } catch {
      if (requestId !== this.requestId || this.visibleUuid !== uuid) return false;
      runInAction(() => {
        this.isLoading = false;
        this.isRefreshing = false;
        this.loadError = 'Не удалось загрузить смену';
      });
      return false;
    }
  };

  setResponsible = async (employeeUuid: string | null) => {
    const detail = this.detail;
    if (!detail || detail.status === 'closed' || this.isBusy) return;
    if (employeeUuid === detail.responsible_employee_uuid) return;

    const uuid = detail.uuid;
    this.responsibleSaving = true;
    this.responsiblePending = employeeUuid;
    this.actionError = null;

    try {
      const updated = await adminShiftsUpdateResponsible(uuid, {
        responsible_employee_uuid: employeeUuid,
      });
      await this.reloadIfVisible(uuid, updated);
    } catch (error) {
      if (this.detail?.uuid !== uuid) return;
      const parsed = mutationMessage(error, 'Не удалось сохранить ответственного');
      runInAction(() => {
        this.actionError = userFacingMutationError(parsed, 'Не удалось сохранить ответственного');
      });
    } finally {
      runInAction(() => {
        this.responsibleSaving = false;
        this.responsiblePending = undefined;
      });
    }
  };

  open = async (uuid: string) => {
    if (this.isBusy) return;
    if (this.detail?.uuid === uuid && this.detail.status !== 'created') return;
    this.isOpening = true;
    this.actionError = null;
    try {
      const updated = await adminShiftsOpen(uuid);
      await this.reloadIfVisible(uuid, updated);
    } catch (error) {
      if (this.visibleUuid !== uuid) return;
      const parsed = mutationMessage(error, 'Не удалось открыть смену');
      runInAction(() => {
        this.actionError = userFacingMutationError(parsed, 'Не удалось открыть смену');
      });
    } finally {
      runInAction(() => {
        this.isOpening = false;
      });
    }
  };

  close = async (uuid: string) => {
    if (this.isBusy) return;
    if (this.detail?.uuid === uuid && this.detail.status !== 'open') return;
    this.isClosing = true;
    this.actionError = null;
    this.closeViolations = [];
    try {
      const updated = await adminShiftsClose(uuid);
      await this.reloadIfVisible(uuid, updated);
    } catch (error) {
      if (this.visibleUuid !== uuid) return;
      const parsed = mutationMessage(error, 'Не удалось закрыть смену');
      const isConflict = error instanceof ShiftApiError && error.statusCode === 409;
      runInAction(() => {
        if (isConflict) {
          const formatted = formatCloseViolationMessages(
            this.detail,
            parsed.violationDetails,
            parsed.details,
          );
          this.closeViolations =
            formatted.length > 0 ? formatted : ['Смену нельзя закрыть. Проверьте состав и заявки на дату.'];
          return;
        }
        this.actionError = userFacingMutationError(parsed, 'Не удалось закрыть смену');
      });
    } finally {
      runInAction(() => {
        this.isClosing = false;
      });
    }
  };

  runMemberAction = async (
    memberUuid: string,
    action: 'start' | 'finish' | 'no_show' | 'return' | 'remove',
  ) => {
    const detail = this.detail;
    if (!detail || detail.status === 'closed' || this.isBusy) return;
    const uuid = detail.uuid;
    this.pendingMemberUuid = memberUuid;
    this.actionError = null;

    try {
      let updated: ShiftDetail;
      if (action === 'start') updated = await adminShiftsMemberStart(uuid, memberUuid);
      else if (action === 'finish') updated = await adminShiftsMemberFinish(uuid, memberUuid);
      else if (action === 'no_show') updated = await adminShiftsMemberNoShow(uuid, memberUuid);
      else if (action === 'return') updated = await adminShiftsMemberReturn(uuid, memberUuid);
      else updated = await adminShiftsMemberRemove(uuid, memberUuid);
      await this.reloadIfVisible(uuid, updated);
    } catch (error) {
      if (this.detail?.uuid !== uuid) return;
      const parsed = mutationMessage(error, 'Не удалось изменить состав смены');
      runInAction(() => {
        this.actionError = userFacingMutationError(parsed, 'Не удалось изменить состав смены');
      });
    } finally {
      runInAction(() => {
        this.pendingMemberUuid = null;
      });
    }
  };

  openTimeModal = (memberUuid: string, start: string, end: string) => {
    this.timeModalMemberUuid = memberUuid;
    this.timeStart = start;
    this.timeEnd = end;
    this.timeError = null;
  };

  closeTimeModal = () => {
    if (this.isSavingTime) return;
    this.timeModalMemberUuid = null;
    this.timeError = null;
  };

  setTimeStart = (value: string) => {
    this.timeStart = value;
  };

  setTimeEnd = (value: string) => {
    this.timeEnd = value;
  };

  saveActualTime = async (timeZone: string) => {
    const detail = this.detail;
    const memberUuid = this.timeModalMemberUuid;
    const shiftUuid = detail?.uuid;
    if (!detail || !memberUuid || !shiftUuid || this.isBusy) return;

    const validationError = validateActualClocks(this.timeStart, this.timeEnd);
    if (validationError) {
      this.timeError = validationError;
      return;
    }

    const actualStart = clockOnDateToIso(detail.date.slice(0, 10), this.timeStart, timeZone);
    const actualEnd = clockOnDateToIso(detail.date.slice(0, 10), this.timeEnd, timeZone);
    if (!actualStart || !actualEnd) {
      this.timeError = 'Укажите время начала и конца';
      return;
    }

    this.isSavingTime = true;
    this.timeError = null;
    try {
      const updated = await adminShiftsMemberActualTime(shiftUuid, memberUuid, {
        actual_start: actualStart,
        actual_end: actualEnd,
      });
      if (this.visibleUuid === shiftUuid && this.timeModalMemberUuid === memberUuid) {
        runInAction(() => {
          this.timeModalMemberUuid = null;
        });
      }
      await this.reloadIfVisible(shiftUuid, updated);
    } catch (error) {
      if (this.visibleUuid !== shiftUuid || this.timeModalMemberUuid !== memberUuid) return;
      const parsed = mutationMessage(error, 'Не удалось изменить время');
      runInAction(() => {
        this.timeError = userFacingMutationError(parsed, 'Не удалось изменить время');
      });
    } finally {
      runInAction(() => {
        this.isSavingTime = false;
      });
    }
  };

  private reloadIfVisible = async (uuid: string, fallback?: ShiftDetail) => {
    if (this.visibleUuid !== uuid) return;
    const loaded = await this.fetch(uuid);
    if (this.visibleUuid !== uuid) return;
    if (!loaded && fallback) {
      runInAction(() => {
        this.detail = fallback;
      });
    }
    if (this.visibleUuid !== uuid) return;
    await this.refreshList();
  };

  private refreshList = async () => {
    const detail = this.detail;
    if (!detail?.service_center_uuid || !detail.date) return;
    const week = weekBounds(parseDateParam(detail.date.slice(0, 10)));
    await shiftsStore.load(week.from, week.to, detail.service_center_uuid);
  };
}

export const shiftViewStore = new ShiftViewStore();
export type { ShiftViewStore };
